/** @jsx jsx */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useTheme, Typography } from '@material-ui/core';

import { ICustomTheme, IFormValue } from 'interfaces';
import Heading from './Heading';

type Theme = {
  theme?: ICustomTheme;
};

const QuestionContainer = styled.div(({ theme }: { theme: ICustomTheme }) => ({
  padding: theme.spacing(3, 0, 0, 3),
}));

const QuestionBlock = styled.div(({ theme }: { theme: ICustomTheme }) => ({
  color: theme.colors.paletteFromBO.main,
  fontWeight: 600,
}));

const Question = styled(Typography)<Theme>(({ theme }) => ({
  display: 'inline-block',
  paddingLeft: theme.spacing(1),
  fontWeight: theme.typography.fontWeightRegular,
  '&:first-letter': {
    textTransform: 'capitalize',
  },
  fontSize: 17,
}));

const Answer = styled(Typography)<Theme>(({ theme }) => ({
  padding: theme.spacing(2, 0, 0, 6),
  fontWeight: 600,
  color: theme.colors.grey.dark,
  '&:first-letter': {
    textTransform: 'capitalize',
  },
  fontSize: 17,
}));

const Questions = ({ questions }: { questions: IFormValue[] }) => {
  return (
    <React.Fragment>
      {questions.map(
        (
          { value, formInput, formInputChoiceValues }: IFormValue,
          index: number,
        ) => {
          return (
            <QuestionContainer key={index}>
              <QuestionBlock>
                {index < 9 ? `0${index + 1}. ` : `${index + 1}. `}
                <Question>{formInput.label}</Question>
              </QuestionBlock>
              <Answer>
                {value || formInputChoiceValues[0].formInputChoice.title}
              </Answer>
            </QuestionContainer>
          );
        },
      )}
    </React.Fragment>
  );
};

function Header({
  nbOfQuestions,
  isMobile,
}: {
  nbOfQuestions: number;
  isMobile: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();

  return (
    <Heading css={{ ...(!isMobile && { padding: theme.spacing(3) }) }}>
      <Typography
        align={isMobile ? 'center' : 'left'}
        css={{ fontStyle: 'italic', fontSize: theme.typography.body2.fontSize }}
      >
        {t('challenges.declaration.history.number_of_questions', {
          count: nbOfQuestions,
        })}
      </Typography>
    </Heading>
  );
}

export default function QuestionsDetail({
  questions,
  isMobile,
}: {
  questions: IFormValue[];
  isMobile: boolean;
}) {
  const nbOfQuestions = questions.length;

  return (
    <div
      css={(theme: ICustomTheme) => ({
        marginBottom: theme.spacing(3),
      })}
    >
      <Header nbOfQuestions={nbOfQuestions} isMobile={isMobile} />
      <Questions questions={questions} />
    </div>
  );
}
